import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import "./index.css";
import { Provider } from "react-redux";
import { Router, Route, Switch } from "react-router";

import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import Moment from "moment";
import MomentUtils from "@date-io/moment";
import { extendMoment } from "moment-range";
import moment from "moment-timezone";
import "moment/locale/nl.js";

moment.tz.setDefault("Europe/Amsterdam");

const extendedMoment = extendMoment(Moment);
extendedMoment.locale("nl");

import PrivateRoute from "./helpers/PrivateRoute";
import NonUserRoute from "./helpers/NonUserRoute";
import store from "./store";
import registerServiceWorker from "./registerServiceWorker";

import DashboardPage from "./pages/DashboardPage";
import MyAccountPage from "./pages/MyAccountPage";

import CompanyPage from "./pages/Company/CompanyPage";
import CreateCompanyPage from "./pages/Company/CreateCompanyPage";
import CompanyDetailPage from "./pages/Company/CompanyDetailPage";
import UpdateCompanyPage from "./pages/Company/UpdateCompanyPage";
import UpdateCompanyEmployeePage from "./pages/Company/UpdateCompanyEmployeePage";

import EmployeePage from "./pages/Employee/EmployeePage";
import CreateEmployeePage from "./pages/Employee/CreateEmployeePage";
import EmployeeDetailPage from "./pages/Employee/EmployeeDetailPage";
import UpdateEmployeePage from "./pages/Employee/UpdateEmployeePage";

import ShiftTypePage from "./pages/ShiftType/ShiftTypePage";
import CreateShiftTypePage from "./pages/ShiftType/CreateShiftTypePage";
import UpdateShiftTypePage from "./pages/ShiftType/UpdateShiftTypePage";

import ProjectPage from "./pages/Project/ProjectPage";
import CreateProjectPage from "./pages/Project/CreateProjectPage";
import ProjectDetailPage from "./pages/Project/ProjectDetailPage";
import UpdateProjectPage from "./pages/Project/UpdateProjectPage";

import CreateShiftPage from "./pages/Shift/CreateShiftPage";
import ShiftDetailPage from "./pages/Shift/ShiftDetailPage";
import UpdateShiftPage from "./pages/Shift/UpdateShiftPage";
import ActiveShiftsPage from "./pages/Shift/ActiveShiftsPage";
import ActiveShiftDetailPage from "./pages/Shift/ActiveShiftDetailPage";
import DuplicateShiftPage from "./pages/Shift/DuplicateShiftPage";

import PushNotificationPage from "./pages/PushNotificationPage";
import LoginPage from "./pages/LoginPage";
import NotFoundPage from "./pages/NotFoundPage";
import CreatePasswordPage from "./pages/CreatePasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";

import SchedulePage from "./pages/SchedulePage";
import ProjectsSchedulePage from "./pages/ProjectsSchedulePage";

import HourRegistrationPage from "./pages/HourRegistrationPage";
import HourRegistrationForInvoicesPage from "./pages/HourRegistrationForInvoicesPage";

import UpdateShiftTypeCompanyPage from "./pages/ShiftTypeCompany/UpdateShiftTypeCompanyPage";
import CreateShiftTypeCompanyPage from "./pages/ShiftTypeCompany/CreateShiftTypeCompanyPage";

import CEShiftReportPage from "./pages/CEShiftReportPage";

// import TermsPage from "./pages/TermsPage";

import axios from "axios";

import { getAppSettings } from "./actions/userActions";

import { AppContext } from "./contexts";

import history from "./history";

import "./i18n";
import defaultAppSettings from "./data/default-app-settings";
import isUberAdmin from "./utils/is-uber-admin";

const credentials = JSON.parse(localStorage.getItem("credentials"));
axios.defaults.headers.common["Authorization"] = credentials
  ? "Bearer " + credentials.token
  : null;

// TODO: polyfill, remove
// MomentUtils.prototype.startOfMonth=MomentUtils.prototype.getStartOfMonth

const App = () => {
  const localSettings = JSON.parse(localStorage.getItem("settings"));
  const [settings, setSettings] = useState(localSettings || defaultAppSettings);

  useEffect(() => {
    const fetchData = async () => {
      const settingsUpdatedAt = localStorage.getItem("settingsUpdatedAt");
      const isUpdatedWithinFiveMinutes = moment(settingsUpdatedAt).isAfter(
        moment().subtract(15, "minutes"),
      );
      if (isUpdatedWithinFiveMinutes) return;

      const appSettings = await getAppSettings()(() => {});
      setSettings(appSettings);
      localStorage.setItem("settings", JSON.stringify(appSettings));
      localStorage.setItem("settingsUpdatedAt", moment().toISOString());
    };

    fetchData();
  }, []);

  const theme = createTheme({
    palette: {
      primary: {
        main: settings.color,
      },
    },
  });

  return (
    <AppContext.Provider value={settings}>
      <MuiThemeProvider theme={theme}>
        <Provider store={store}>
          <MuiPickersUtilsProvider
            utils={MomentUtils}
            locale="nl"
            moment={moment}
          >
            <Router history={history}>
              <Switch>
                <PrivateRoute exact path="/" component={DashboardPage} />
                <PrivateRoute
                  exact
                  path="/my-account"
                  component={MyAccountPage}
                />

                <PrivateRoute exact path="/company" component={CompanyPage} />
                <PrivateRoute
                  exact
                  path="/company/create"
                  component={CreateCompanyPage}
                />
                <PrivateRoute
                  exact
                  path="/company/:id"
                  component={CompanyDetailPage}
                />
                <PrivateRoute
                  exact
                  path="/company/:id/update"
                  component={UpdateCompanyPage}
                />
                <PrivateRoute
                  exact
                  path="/company/:company_email/employee/:employee_email/update"
                  component={UpdateCompanyEmployeePage}
                />

                <PrivateRoute
                  exact
                  path="/company/:company_email/employee/:employee_email"
                  component={EmployeeDetailPage}
                />

                <PrivateRoute exact path="/employee" component={EmployeePage} />
                <PrivateRoute
                  exact
                  path="/employee/create"
                  component={CreateEmployeePage}
                />
                <PrivateRoute
                  exact
                  path="/employee/:employee_email"
                  component={EmployeeDetailPage}
                />
                <PrivateRoute
                  exact
                  path="/employee/:employee_email/update"
                  component={UpdateEmployeePage}
                />

                <PrivateRoute
                  exact
                  path="/shifttype"
                  component={ShiftTypePage}
                />
                <PrivateRoute
                  exact
                  path="/shifttype/create"
                  component={CreateShiftTypePage}
                />
                <PrivateRoute
                  exact
                  path="/shifttype/:id/update"
                  component={UpdateShiftTypePage}
                />

                <PrivateRoute exact path="/project" component={ProjectPage} />
                <PrivateRoute
                  exact
                  path="/project/create"
                  component={CreateProjectPage}
                />
                <PrivateRoute
                  exact
                  path="/company/:company_email/project/:project_id"
                  component={ProjectDetailPage}
                />
                <PrivateRoute
                  exact
                  path="/project/:project_id/update"
                  component={UpdateProjectPage}
                />

                <PrivateRoute
                  exact
                  path="/company/:company_email/project/:project_id/shift/create"
                  component={CreateShiftPage}
                />
                <PrivateRoute
                  exact
                  path="/shift/:shift_id/update"
                  component={UpdateShiftPage}
                />
                <PrivateRoute
                  exact
                  path="/shift/explore"
                  component={ActiveShiftsPage}
                />
                <PrivateRoute
                  exact
                  path="/shift/:shift_id"
                  component={ShiftDetailPage}
                />
                <PrivateRoute
                  exact
                  path="/shift/:shift_id/duplicate"
                  component={DuplicateShiftPage}
                />
                <PrivateRoute
                  exact
                  path="/shift/active/:shift_id"
                  component={ActiveShiftDetailPage}
                />

                <PrivateRoute exact path="/schedule" component={SchedulePage} />
                <PrivateRoute
                  exact
                  path="/projects_schedule"
                  component={ProjectsSchedulePage}
                />
                <PrivateRoute
                  exact
                  path="/hour_registration"
                  component={HourRegistrationPage}
                />

                <PrivateRoute
                  exact
                  path="/hour_registration_for_invoices"
                  component={
                    isUberAdmin ? HourRegistrationForInvoicesPage : NotFoundPage
                  }
                />

                <PrivateRoute
                  exact
                  path="/shifttype/:shifttype_id/company/:company_email/update"
                  component={UpdateShiftTypeCompanyPage}
                />

                <PrivateRoute
                  exact
                  path="/shifttype/:shifttype_id/company/create"
                  component={CreateShiftTypeCompanyPage}
                />

                <PrivateRoute
                  exact
                  path="/company/:company_email/employee/:employee_email/shift/:shift_id/report"
                  component={CEShiftReportPage}
                />

                <PrivateRoute
                  exact
                  path="/push-notification"
                  component={PushNotificationPage}
                />
                <NonUserRoute exact path="/login" component={LoginPage} />
                <NonUserRoute
                  exact
                  path="/create-password/:token"
                  component={CreatePasswordPage}
                />
                <NonUserRoute
                  exact
                  path="/forgot-password"
                  component={ForgotPasswordPage}
                />

                {/* TODO: uncomment for terms and conditions */}
                {/* <NonUserRoute
                  exact
                  path="/terms-and-conditions"
                  component={TermsPage}
                /> */}

                <Route path="*" component={NotFoundPage} />
              </Switch>
            </Router>
          </MuiPickersUtilsProvider>
        </Provider>
      </MuiThemeProvider>
    </AppContext.Provider>
  );
};

const startApp = () => {
  ReactDOM.render(<App />, document.getElementById("root"));

  registerServiceWorker();
};

if (window.cordova) {
  document.addEventListener("deviceready", startApp, false);
} else {
  startApp();
}



// WEBPACK FOOTER //
// ./src/index.js